import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDialog } from "@adora/shared/hooks/use-dialog";
import { Box, Button, Menu, MenuItem, Table, TextField } from "flux";
// TODO: migrate useBrands to TanStack Query (legacy SWR)
import useBrands from "../hooks/use-brands";
import { Brand } from "@adora/shared/types/brand";
import LaunchExperimentDialog from "../components/launch-experiments-dialog";
import BulkGenerationDialog from "../components/bulk-generation-dialog";
import Page from "@adora/shared/components/layout/page";
import { CreateBrandDialog, EditBrandDialog } from "../components/brand-dialog";

const PAGE_SIZE = 25;

const Brands: React.FC = () => {
  const { brands, isLoading } = useBrands();
  const { openDialog } = useDialog();

  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedBrandId, setSelectedBrandId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(0);

  const filteredBrands = useMemo(() => {
    if (!brands) return undefined;
    if (!searchQuery.trim()) return brands;
    const query = searchQuery.toLowerCase();
    return brands.filter(
      (b) =>
        b.name?.toLowerCase().includes(query) ||
        b.id.toString().includes(query) ||
        b.s3Bucket?.toLowerCase().includes(query)
    );
  }, [brands, searchQuery]);

  const totalPages = filteredBrands ? Math.ceil(filteredBrands.length / PAGE_SIZE) : 0;
  const safePage = totalPages > 0 ? Math.min(currentPage, totalPages - 1) : 0;

  useEffect(() => {
    setCurrentPage((prev) => {
      if (totalPages === 0) return 0;
      return Math.min(prev, totalPages - 1);
    });
  }, [totalPages]);

  const paginatedBrands = useMemo(() => {
    if (!filteredBrands) return undefined;
    const start = safePage * PAGE_SIZE;
    return filteredBrands.slice(start, start + PAGE_SIZE);
  }, [filteredBrands, safePage]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(0);
  }, []);

  const handlePreviousPage = useCallback(() => {
    setCurrentPage((p) => Math.max(0, p - 1));
  }, []);

  const handleNextPage = useCallback(() => {
    setCurrentPage((p) => Math.min(totalPages - 1, p + 1));
  }, [totalPages]);

  const handleRowClick = (brandId: number) => {
    navigate(`/brands/${brandId}/ad-asset-groups`);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, brandId: number) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedBrandId(brandId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedBrandId(null);
  };

  const handleViewAdAssets = () => {
    if (selectedBrandId) {
      navigate(`/brands/${selectedBrandId}/ad-asset-groups`);
    }
    handleMenuClose();
  };

  const handleLaunchExperiment = async () => {
    if (selectedBrandId) {
      handleMenuClose();
      const confirm = await openDialog(LaunchExperimentDialog, { brandId: selectedBrandId });
      if (confirm) {
        navigate("/batches");
      }
    }
  };

  const handleViewExperiments = () => {
    if (selectedBrandId) {
      navigate(`/brands/${selectedBrandId}/experiments`);
    }
    handleMenuClose();
  };

  const handleViewReports = () => {
    if (selectedBrandId) {
      navigate(`/brands/${selectedBrandId}/reports_settings`);
    }
    handleMenuClose();
  };

  const handleManageTokens = () => {
    if (selectedBrandId) {
      navigate(`/brands/${selectedBrandId}/tokens`);
    }
    handleMenuClose();
  };

  const handleBulkUploadImages = async () => {
    if (selectedBrandId) {
      handleMenuClose();
      const confirm = await openDialog(BulkGenerationDialog, { brandId: selectedBrandId });
      if (confirm) {
        navigate("/batches");
      }
    }
  };

  const columns = [
    { field: "id", value: "ID", width: 100 },
    { field: "name", value: "Name", width: 500 },
    { field: "s3Bucket", value: "S3 Bucket", width: 400 },
    { field: "status", value: "Status", width: 150 },
    {
      field: "creationTime",
      value: "Created At",
      width: 200,
      renderCell: (row: Brand) =>
        row.creationTime
          ? row.creationTime.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
          : "—"
    },
    {
      field: "actions",
      value: "Actions",
      width: 50,
      renderCell: (row: Brand) => (
        <Button
          variant="text"
          color="secondary"
          size="medium"
          onClick={(e) => handleMenuOpen(e, row.id)}
          dataDogIdentifier="brands page ellipsis action"
        >
          …
        </Button>
      )
    }
  ];

  async function handleCreateBrand() {
    await openDialog(CreateBrandDialog);
  }

  async function handleEditBrand() {
    if (selectedBrandId) {
      const brand = brands?.find((b) => b.id === selectedBrandId);
      if (brand) {
        handleMenuClose();
        await openDialog(EditBrandDialog, { brand });
      }
    }
  }

  return (
    <Page
      title="Brands"
      titleBarActions={
        <Button variant="contained" onClick={handleCreateBrand} dataDogIdentifier="brands page create brand">
          Create Brand
        </Button>
      }
    >
      <Box>
        <Box sx={{ mb: 2, maxWidth: 400 }}>
          <TextField
            type="text"
            placeholder="Search brands by name, ID, or S3 bucket..."
            value={searchQuery}
            onChange={handleSearchChange}
            fullWidth
            size="small"
            dataDogIdentifier="brands page search"
          />
        </Box>
        <Table
          columns={columns}
          rows={paginatedBrands}
          handleRowClick={handleRowClick}
          isLoading={isLoading}
          dataDogIdentifier="brands"
        />
        {totalPages > 1 && (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 1, py: 2 }}>
            <Box component="span" sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
              Showing {safePage * PAGE_SIZE + 1}–{Math.min((safePage + 1) * PAGE_SIZE, filteredBrands?.length ?? 0)} of{" "}
              {filteredBrands?.length ?? 0} brands
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handlePreviousPage}
                disabled={safePage === 0}
                dataDogIdentifier="brands page previous"
              >
                Previous
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleNextPage}
                disabled={safePage >= totalPages - 1}
                dataDogIdentifier="brands page next"
              >
                Next
              </Button>
            </Box>
          </Box>
        )}
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        sx={{
          "& .MuiPaper-root": {
            minWidth: "110px"
          }
        }}
      >
        <MenuItem size="medium" onClick={handleViewAdAssets}>
          View Ad Asset Groups
        </MenuItem>
        <MenuItem size="medium" onClick={handleLaunchExperiment}>
          Launch Experiment
        </MenuItem>
        <MenuItem size="medium" onClick={handleViewExperiments}>
          View Experiments
        </MenuItem>
        <MenuItem size="medium" onClick={handleViewReports}>
          Report Settings
        </MenuItem>
        <MenuItem size="medium" onClick={handleManageTokens}>
          Manage Tokens
        </MenuItem>
        <MenuItem size="medium" onClick={handleBulkUploadImages}>
          Bulk Image Generation - CSV Upload
        </MenuItem>
        <MenuItem size="medium" onClick={handleEditBrand}>
          Edit
        </MenuItem>
      </Menu>
    </Page>
  );
};

export default Brands;
